
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "./BaseForm";
import { ProfessionalIndemnityFormData, FormProps } from "@/types/forms";

type ProfessionalIndemnityFormProps = FormProps<ProfessionalIndemnityFormData>;

const ProfessionalIndemnityForm = ({ onSubmit, initialData = {}, buttonText = "Continue" }: ProfessionalIndemnityFormProps) => {
  const [formData, setFormData] = useState<ProfessionalIndemnityFormData>({
    professionType: initialData.professionType || "",
    annualRevenue: initialData.annualRevenue || "",
    numberOfProfessionals: initialData.numberOfProfessionals || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.professionType) newErrors.professionType = "Profession type is required";
    if (!formData.annualRevenue) newErrors.annualRevenue = "Annual revenue is required";
    if (!formData.numberOfProfessionals) newErrors.numberOfProfessionals = "Number of professionals is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const updateField = <K extends keyof ProfessionalIndemnityFormData>(field: K, value: ProfessionalIndemnityFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField label="Profession / service type" required>
        <Input
          placeholder="Enter your profession or service type"
          value={formData.professionType}
          onChange={(e) => updateField('professionType', e.target.value)}
        />
        {errors.professionType && <p className="text-red-500 text-sm">{errors.professionType}</p>}
      </FormField>

      <FormField label="Annual revenue (AED)" required>
        <Input
          type="number"
          placeholder="Enter annual revenue in AED"
          value={formData.annualRevenue}
          onChange={(e) => updateField('annualRevenue', e.target.value)}
        />
        {errors.annualRevenue && <p className="text-red-500 text-sm">{errors.annualRevenue}</p>}
      </FormField>

      <FormField label="Number of professionals to be covered" required>
        <Input
          type="number"
          placeholder="Enter number of professionals"
          value={formData.numberOfProfessionals}
          onChange={(e) => updateField('numberOfProfessionals', e.target.value)}
        />
        {errors.numberOfProfessionals && <p className="text-red-500 text-sm">{errors.numberOfProfessionals}</p>}
      </FormField>

      <Button type="submit" className="w-full">
        {buttonText}
      </Button>
    </form>
  );
};

export default ProfessionalIndemnityForm;
